import React, { useEffect, useState } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import {
  FaBan,
  FaCartArrowDown,
  FaCog,
  FaFileUpload,
  FaNotEqual,
  FaPrint,
  FaPalette,
  FaSearch,
  FaServer,
  FaDesktop,
  FaSignOutAlt,
  FaUndo,
  FaUserFriends,
  FaTruck,
} from "react-icons/fa";
import MenuIcon from "@mui/icons-material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import OrdersPage from "./OrdersPage";
import PrintsPage from "./PrintsPage";
import PickupReturnPage from "./PickupReturnPage";
import AccountsPage from "./AccountsPage";
import NotScannedDialog from "./NotScannedDialog";
import ScanSummary from "./ScanSummary";
import api from "../Api";
import Scanner from "../Scanner.js";
import GraphSection from "./GraphSection";
import UploadSKUsAdmin from "./UploadSKUsAdmin";
import SkuColorAuditPage from "./SkuColorAuditPage";
import DisabledSKUsPage from "./DisabledSKUsPage";
import SystemSettingsPage from "./SystemSettingsPage";
import CancelPacketsPage from "./CancelPacketsPage";
import ReturnClaimsPage from "./ReturnClaimsPage";
import SealsPage from "./SealsPage";
import MyntraAutomationPage from "./MyntraAutomationPage";
import MyntraAdvPage from "./MyntraAdvPage";
import FlipkartConsolePage from "./FlipkartConsolePage";
import MyntraConsolePage from "./MyntraConsolePage";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { clearAuthSession, hasPermission } from "../auth";
import { getAppDateString } from "../utils/date";
import "./AdminPanel.css";

const moduleItems = [
  { key: "orders", path: "orders", label: "Orders", icon: <FaCartArrowDown color="white" /> },
  { key: "prints", path: "prints", label: "Prints", icon: <FaPrint color="white" /> },
  { key: "pickup_return", permission: "prints", path: "pickup-return", label: "Pickup / Return", icon: <FaTruck color="white" /> },
  { key: "scanner", path: "scanner", label: "Scan", icon: <FaSearch color="white" /> },
  { key: "cancel_packets", path: "cancel-packets", label: "Cancel Packets", icon: <FaBan color="white" /> },
  { key: "return_claims", path: "return-claims", label: "Return Claims", icon: <FaUndo color="white" /> },
  { key: "accounts", path: "accounts", label: "Accounts", icon: <FaUserFriends color="white" /> },
  { key: "sku_upload", path: "sku-upload", label: "SKU Upload", icon: <FaFileUpload color="white" /> },
  { key: "sku_color_audit", permission: "sku_upload", path: "sku-color-audit", label: "SKU Color Audit", icon: <FaPalette color="white" /> },
  { key: "myntra_automation", path: "myntra-automation", label: "Myntra Automation", icon: <FaServer color="white" /> },
  { key: "flipkart_console", path: "flipkart-console", label: "Flipkart Console", icon: <FaDesktop color="white" /> },
  { key: "myntra_console", path: "myntra-console", label: "Myntra Console", icon: <FaDesktop color="white" /> },
  { key: "settings", path: "settings", label: "Settings", icon: <FaCog color="white" /> },
];

export default function AdminPanel() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [openNsDialog, setOpenNsDialog] = useState(false);
  const [nsInitialGroup, setNsInitialGroup] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [selectedDate, setSelectedDate] = useState(getAppDateString());
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [systemMetrics, setSystemMetrics] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const can = (permission) => hasPermission(permission);

  const blocked = (
    <Box sx={{ p: 4, textAlign: "center" }}>
      <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
        Access not allowed
      </Typography>
      <Typography color="text.secondary">
        Ask super admin to enable this module for your login.
      </Typography>
    </Box>
  );

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await api.get("/api/accounts");
        setAccounts(response.data);
      } catch (error) {
        console.error("Failed to load accounts", error);
      }
    };

    fetchAccounts();
  }, []);

  useEffect(() => {
    let active = true;

    const fetchSystemMetrics = async () => {
      try {
        const response = await api.get("/api/system-metrics");
        if (active) {
          setSystemMetrics(response.data);
        }
      } catch {
        // Keep the last good reading visible if the short status poll misses once.
      }
    };

    fetchSystemMetrics();
    const intervalId = setInterval(fetchSystemMetrics, 10000);

    return () => {
      active = false;
      clearInterval(intervalId);
    };
  }, []);

  const handlePageChange = (path) => {
    navigate(`/admin/${path}`);
    setMenuOpen(false);
  };

  const closeActionMenu = () => setActionMenuAnchor(null);
  const runMobileAction = (action) => {
    closeActionMenu();
    action();
  };

  const topActions = [
    can("scanner") && {
      key: "scan",
      label: "SCAN",
      color: "success",
      icon: <FaSearch />,
      action: () => navigate("/admin/scanner"),
    },
    can("scanner") && {
      key: "not-scanned",
      label: "Not Scanned",
      color: "secondary",
      icon: <FaNotEqual />,
      action: () => { setNsInitialGroup(""); setOpenNsDialog(true); },
    },
    can("orders") && {
      key: "orders",
      label: "Orders",
      color: "primary",
      icon: <FaCartArrowDown />,
      action: () => navigate("/admin/orders"),
    },
    can("prints") && {
      key: "pickup-return",
      label: "Pickup / Return",
      color: "primary",
      icon: <FaTruck />,
      action: () => navigate("/admin/pickup-return"),
    },
    can("flipkart_console") && {
      key: "flipkart-console",
      label: "Flipkart Console",
      color: "primary",
      icon: <FaDesktop />,
      action: () => navigate("/admin/flipkart-console"),
    },
    can("myntra_console") && {
      key: "myntra-console",
      label: "Myntra Console",
      color: "primary",
      icon: <FaDesktop />,
      action: () => navigate("/admin/myntra-console"),
    },
  ].filter(Boolean);

  const metricRows = [
    ["CPU", systemMetrics?.cpu],
    ["RAM", systemMetrics?.ram],
    ["SSD", systemMetrics?.ssd],
  ];

  return (
    <div className="admin-container">
      <AppBar position="fixed" sx={{ backgroundColor: "#5A5A5A" }}>
        {/* Kept deliberately short: this bar is fixed on every admin page, so its
            height comes straight out of the working area below it (worst on the
            Flipkart Console, where the remote screen gets whatever is left). */}
        <Toolbar sx={{ minHeight: { xs: 48, sm: 52, lg: 56 }, px: { xs: 1, sm: 2 }, gap: { xs: 0.5, sm: 1 } }}>
          <IconButton color="inherit" onClick={() => setMenuOpen(prev => !prev)} edge="start" sx={{ flexShrink: 0 }}>
            <MenuIcon />
          </IconButton>

          <Box sx={{ display: "flex", alignItems: "center", ml: { xs: 0.5, sm: 1 }, flexGrow: 1, minWidth: 0 }}>
            <Box
              onClick={() => navigate("/admin")}
              sx={{ cursor: "pointer", display: "flex", alignItems: "center", flexShrink: 0 }}
            >
              <Box
                component="img"
                src="/slowlorislogoonlyt.png"
                alt="Slowloris Logo"
                sx={{
                  // sized to fit inside the bar rather than setting its height
                  width: { xs: 34, sm: 38, lg: 44 },
                  height: { xs: 34, sm: 38, lg: 44 },
                  mr: { xs: 0.75, sm: 1, lg: 1.5 },
                }}
              />
            </Box>
            <Typography
              variant="h5"
              noWrap
              sx={{
                fontWeight: "bold",
                fontFamily: "'Caveat', 'Patrick Hand', 'Comic Sans MS', cursive",
                fontStyle: "normal",
                fontSize: { xs: 17, sm: 20, lg: 23 },
                minWidth: 0,
              }}
            >
              Admin Panel
            </Typography>
          </Box>

          <Box sx={{ display: { xs: "none", lg: "flex" }, gap: 1, flexWrap: "nowrap", alignItems: "center" }}>
            {topActions.map(action => (
              <Button
                key={action.key}
                variant="contained"
                color={action.color}
                startIcon={action.icon}
                onClick={action.action}
                size="small"
                sx={{ whiteSpace: "nowrap" }}
              >
                {action.label}
              </Button>
            ))}
          </Box>

          <Box
            sx={{
              display: { xs: "none", lg: "grid" },
              gridTemplateColumns: "auto auto",
              columnGap: 0.75,
              rowGap: 0,
              alignItems: "center",
              flexShrink: 0,
              bgcolor: "rgba(0, 0, 0, 0.22)",
              border: "1px solid rgba(255, 255, 255, 0.12)",
              borderRadius: 1,
              px: 0.75,
              py: 0.35,
              minWidth: 72,
              lineHeight: 1.05,
              fontSize: 10,
            }}
          >
            {metricRows.map(([label, value]) => (
              <React.Fragment key={label}>
                <Box component="span" sx={{ color: "rgba(255,255,255,0.58)", fontWeight: 800 }}>
                  {label}
                </Box>
                <Box component="span" sx={{ color: "rgba(255,255,255,0.82)", fontWeight: 800, textAlign: "right" }}>
                  {Number.isFinite(value) ? `${value}%` : "--"}
                </Box>
              </React.Fragment>
            ))}
          </Box>

          <Box sx={{ display: { xs: "flex", lg: "none" }, alignItems: "center", flexShrink: 0 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              endIcon={<MoreVertIcon />}
              onClick={(event) => setActionMenuAnchor(event.currentTarget)}
              sx={{ minWidth: 0, px: { xs: 1, sm: 1.5 }, whiteSpace: "nowrap" }}
            >
              Actions
            </Button>
            <Menu
              anchorEl={actionMenuAnchor}
              open={Boolean(actionMenuAnchor)}
              onClose={closeActionMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: { minWidth: 210 } }}
            >
              {topActions.map(action => (
                <MenuItem key={action.key} onClick={() => runMobileAction(action.action)}>
                  <ListItemIcon sx={{ minWidth: 34, color: "inherit" }}>
                    {action.icon}
                  </ListItemIcon>
                  <ListItemText primary={action.label} />
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={menuOpen}
        onClose={() => setMenuOpen(prev => !prev)}
        PaperProps={{ style: { backgroundColor: "#003366", color: "white" } }}
      >
        <div style={{ width: 250, display: "flex", flexDirection: "column", height: "100vh" }}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "10px" }}>
            <Typography variant="h6" style={{ color: "white" }}>
              MENU
            </Typography>
            <IconButton onClick={() => setMenuOpen(prev => !prev)} style={{ color: "white" }}>
              <CloseIcon />
            </IconButton>
          </div>
          <Divider style={{ backgroundColor: "white" }} />
          <List sx={{ flex: 1, overflowY: "auto" }}>
            {moduleItems
              .filter(item => can(item.permission || item.key))
              .map(item => (
                <ListItem button onClick={() => handlePageChange(item.path)} key={item.key}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItem>
              ))}
          </List>
          <Divider style={{ backgroundColor: "rgba(255,255,255,0.35)" }} />
          <Box sx={{ p: 1.25 }}>
            <Button
              fullWidth
              variant="contained"
              color="error"
              startIcon={<FaSignOutAlt />}
              onClick={() => {
                setMenuOpen(false);
                setLogoutDialogOpen(true);
              }}
              sx={{ justifyContent: "flex-start" }}
            >
              Logout
            </Button>
          </Box>
        </div>
      </Drawer>

      {/* must track the Toolbar's minHeight above -- the bar is fixed, so this is
          what stops the content sliding underneath it */}
      <Box component="main" sx={{ mt: { xs: "48px", sm: "52px", lg: "56px" }, width: "100%" }}>
        <Routes>
          <Route path="orders" element={can("orders") ? <OrdersPage selectedDate={selectedDate} setSelectedDate={setSelectedDate} /> : blocked} />
          <Route path="prints" element={can("prints") ? <PrintsPage selectedDate={selectedDate} setSelectedDate={setSelectedDate} /> : blocked} />
          <Route path="pickup-return" element={can("prints") ? <PickupReturnPage selectedDate={selectedDate} setSelectedDate={setSelectedDate} /> : blocked} />
          <Route path="scanner" element={can("scanner") ? <Scanner /> : blocked} />
          <Route path="cancel-packets" element={can("cancel_packets") ? <CancelPacketsPage /> : blocked} />
          <Route path="return-claims" element={can("return_claims") ? <ReturnClaimsPage /> : blocked} />
          <Route path="seals" element={can("prints") ? <SealsPage /> : blocked} />
          <Route path="accounts" element={can("accounts") ? <AccountsPage /> : blocked} />
          <Route path="sku-upload" element={can("sku_upload") ? <UploadSKUsAdmin /> : blocked} />
          <Route path="sku-color-audit" element={can("sku_upload") ? <SkuColorAuditPage /> : blocked} />
          <Route path="excluded-skus" element={can("sku_upload") ? <DisabledSKUsPage /> : blocked} />
          <Route path="disabled-skus" element={can("sku_upload") ? <DisabledSKUsPage /> : blocked} />
          <Route path="myntra-automation" element={can("myntra_automation") ? <MyntraAutomationPage /> : blocked} />
          <Route path="flipkart-console" element={can("flipkart_console") ? <FlipkartConsolePage /> : blocked} />
          <Route path="myntra-console" element={can("myntra_console") ? <MyntraConsolePage /> : blocked} />
          <Route path="myntra-adv" element={can("myntra_automation") ? <MyntraAdvPage /> : blocked} />
          <Route path="settings" element={can("settings") ? <SystemSettingsPage /> : blocked} />
        </Routes>

        {location.pathname.replace(/\/+$/, "") === "/admin" && (
          <Box sx={{ padding: "20px 30px 0px 30px" }}>
            {can("dashboard") ? (
              <>
                <GraphSection />
                <ScanSummary
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  readOnlyDate={false}
                  accounts={accounts}
                  onOpenNotScanned={(group) => { setNsInitialGroup(group || ""); setOpenNsDialog(true); }}
                />
              </>
            ) : blocked}
          </Box>
        )}
      </Box>

      {openNsDialog && (
        <NotScannedDialog
          open={openNsDialog}
          onClose={() => setOpenNsDialog(false)}
          accounts={accounts}
          initialGroup={nsInitialGroup}
        />
      )}

      <Dialog open={logoutDialogOpen} onClose={() => setLogoutDialogOpen(false)}>
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to logout?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={async () => {
              try {
                await api.post("/api/auth/logout");
              } catch (error) {
                console.warn("Logout request failed:", error.message);
              }
              clearAuthSession();
              toast.success("Logged out successfully");
              setTimeout(() => {
                navigate("/admin/login");
              }, 1000);
            }}
            color="error"
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
